import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { UserRoleMedicalServiceStore } from './user-role-medical-service.store';
import { SwitchInputComponent } from '../../../../kit/input/switch-input/switch-input.component';
import { SelectUserGroupComponent } from '../select-user-group/select-user-group.component';
import { fb, routeParams, router, translate } from '../../../../../modules/shared/shared.global-service';
import { SelectUserRuleComponent } from '../select-user-rule/select-user-rule.component';
import { FormGroup } from '@angular/forms';
import { ServiceGridItemObject, UserGroupUpsertInput, UserRuleUpsertInput } from '@it2go/types';
import { parseRuleId, ruleId } from '../select-user-rule/select-user-rule.store';
import { GroupUserObject } from '@libs/types/src/user/object-type/group-user.object';
import { KeyValuePipe } from '@angular/common';
import { UserUpdateInput } from '@libs/types/src/user/input-type/user/user-update.input';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { featureOn } from '../../../../../feature-flag';

@Component({
    selector: 'app-user-role-medical-service',
    templateUrl: './user-role-medical-service.component.html',
    styleUrls: ['./user-role-medical-service.component.scss'],
    providers: [UserRoleMedicalServiceStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SwitchInputComponent,
        SelectUserGroupComponent,
        SelectUserRuleComponent,
        KeyValuePipe,
        ButtonComponent,
    ],
})
export class UserRoleMedicalServiceComponent {

    protected readonly featureOn = featureOn;
    public readonly store = inject(UserRoleMedicalServiceStore);

    public commonForm = fb.group({
        perService: fb.control(false),
    });
    public form = this.createForm({});

    protected serviceForms = computed(() => {
        const groups = this.store.userGroups();
        const employedIn = this.store.employedIn();
        const allowedServices = this.store.globalGlobalStore.organizationStore.services();

        const forms: Record<number, FormGroup> = {};

        allowedServices.forEach((service: ServiceGridItemObject) => {
            forms[service.id] = this.createForm(service);
            if (!employedIn.includes(service.id)) {
                forms[service.id].disable();
            }
        });

        const perService = !!JSON.parse(groups?.[0]?.data || '{}')?.serviceIds;
        if (perService) {
            groups.forEach((it) => {
                const serviceIds = JSON.parse(it.data || '{}').serviceIds || [];
                serviceIds.forEach((serviceId: number) => {
                    if (!forms[serviceId]) {
                        forms[serviceId] = this.createForm({ id: serviceId });
                        forms[serviceId].disable();
                    }
                    const form = forms[serviceId];
                    if (!employedIn.includes(serviceId)) {
                        form.patchValue({
                            groupIds: [...form.value.groupIds, it.group.id],
                        });
                    }
                });
            });
        }

        return forms;
    });

    constructor() {
        effect(() => {
            const groups = this.store.data.getGroupUsers()?.['items'] || [];
            const perService = !!JSON.parse(groups?.[0]?.data || '{}')?.serviceIds;
            this.commonForm.patchValue({ perService });

            const publicGroups = groups.filter((it: GroupUserObject) => !it.group.isPrivate);
            const privateGroups = groups.filter((it: GroupUserObject) => it.group.isPrivate);

            let updateDisabledGroups = false;

            if (perService) {
                Object.values(this.serviceForms()).forEach((it) => {
                    it.patchValue({ groupIds: [], ruleIds: [] });
                });

                publicGroups.forEach((it: GroupUserObject) => {
                    const serviceIds = JSON.parse(it.data || '{}').serviceIds || [];
                    serviceIds.forEach((serviceId: number) => {
                        const form = this.serviceForms()?.[serviceId];
                        if (form) {
                            form.patchValue({
                                groupIds: [...form.value.groupIds, it.group.id],
                            });
                            if (this.store.employmentUpdated && form.disabled && form.value.groupIds.length > 0) {
                                updateDisabledGroups = true;
                                form.patchValue({ groupIds: [] });
                            }
                        }
                    });
                });
                privateGroups.forEach((it: GroupUserObject) => {
                    const serviceIds = JSON.parse(it.data || '{}').serviceIds || [];
                    serviceIds.forEach((serviceId: number) => {
                        const form = this.serviceForms()?.[serviceId];
                        if (form) {
                            form.patchValue({
                                ruleIds: [...form.value.ruleIds, ...it.group.rules.map((ij) => ruleId(ij.resource, ij.action))],
                            });
                            if (this.store.employmentUpdated && form.disabled && form.value.ruleIds.length > 0) {
                                updateDisabledGroups = true;
                                form.patchValue({ ruleIds: [] });
                            }
                        }
                    });
                });
                if (updateDisabledGroups) {
                    // when employment was updated or deleted, groups and roles controls are disabled and when they have values, they should be removed
                    this.updateUserGroups();
                    this.store.employmentUpdated = false;
                }
            } else {
                this.form.patchValue({
                    groupIds: publicGroups.map((it: GroupUserObject) => it.group.id),
                    ruleIds: privateGroups.flatMap((it: GroupUserObject) => {
                        return it.group.rules.map((ij) => ruleId(ij.resource, ij.action));
                    }),
                });
            }
        });
    }

    public getRoles(): { groups: UserGroupUpsertInput[], rules: UserRuleUpsertInput[], perService: boolean } {
        const roles = {
            groups: [] as any[],
            rules: [] as any[],
            perService: this.commonForm.value.perService ?? false,
        };
        if (roles.perService) {
            Object.values(this.serviceForms()).forEach((it) => {
                this.parseForm(it, roles);
            });

            // Merge same groups into a single with multiple serviceIds
            for (let i = roles.groups.length - 1; i >= 0; i--) {
                const group: any = roles.groups[i];
                const firstGroup = roles.groups.findIndex((it) => it.id === group.id);
                if (firstGroup < i) {
                    roles.groups[firstGroup].data.serviceIds.push(group.data.serviceIds[0]);
                    roles.groups.splice(i, 1);
                }
            }
            for (let i = roles.rules.length - 1; i >= 0; i--) {
                const rule: any = roles.rules[i];
                const firstGroup = roles.rules.findIndex((it) => it.action === rule.action && it.resource === rule.resource);
                if (firstGroup < i) {
                    roles.rules[firstGroup].data.serviceIds.push(rule.data.serviceIds[0]);
                    roles.rules.splice(i, 1);
                }
            }
        } else {
            this.parseForm(this.form, roles);
        }

        roles.groups.forEach((it) => {
            it.data = JSON.stringify(it.data || {});
        });
        roles.rules.forEach((it) => {
            it.data = JSON.stringify(it.data || {});
        });

        return roles;
    }

    protected async editGroups(): Promise<void> {
        await router.navigateByUrl(`/content/organizations/${this.store.globalGlobalStore.organizationStore.selectedId()}/acl`);
    }

    protected async copyPermissionsToChildOrganizations(): Promise<void> {
        const userId = routeParams.id();
        if (!userId) return;

        const { rules, groups } = this.getRoles();
        await this.store.api.copyUserPermissionsToChildOrganizations({
            userId: String(userId),
            rules,
            groups,
        });
        contentGlobalSuccess.next('acl.alert.permissionsCopiedToChildOrganizations');
    }

    protected onSwitchForms() {
        const groups = this.store.userGroups();
        const employedIn = this.store.employedIn();

        if (!this.commonForm.value.perService) {
            groups.forEach(group => {
                const serviceIds = JSON.parse(group.data || '{}').serviceIds || [];
                if (group.group.isPrivate && serviceIds.length === 0 || serviceIds.some((it: number) => employedIn.includes(it))) {
                    if (group.group.isPrivate) {
                        this.form.patchValue({
                            ruleIds: Array.from(
                                new Set(
                                    [...this.form.value.ruleIds, ...group.group.rules
                                        .map((ij) => ruleId(ij.resource, ij.action))
                                    ],
                                ),
                            ),
                        });
                    } else {
                        this.form.patchValue({
                            groupIds: [...this.form.value.groupIds, group.group.id],
                        });
                    }
                }
            })
        } else {
            Object.entries(this.serviceForms()).forEach(([serviceId, form]) => {
                form.patchValue({ groupIds: [], ruleIds: [] });
                groups.forEach(group => {
                    if (employedIn.includes(Number(serviceId))) {
                        if (group.group.isPrivate) {
                            form.patchValue({
                                ruleIds: Array.from(
                                    new Set(
                                        [...this.form.value.ruleIds, ...group.group.rules
                                            .map((ij) => ruleId(ij.resource, ij.action))
                                        ],
                                    ),
                                ),
                            });
                        } else {
                            form.patchValue({
                                groupIds: [...form.value.groupIds, group.group.id],
                            });
                        }
                    }
                })
            });
        }
    }

    private createForm(service: { id?: number, name?: string }): FormGroup {
        return fb.group({
            name: fb.control<string>(service.name || translate.instant('service.hiddenService')),
            ruleIds: fb.control<string[]>([]),
            groupIds: fb.control<number[]>([]),
            serviceId: fb.control<number>(service.id || 0),
        });
    }

    private parseForm(form: FormGroup, roles: { groups: UserGroupUpsertInput[], rules: UserRuleUpsertInput[] }): void {
        form.value.groupIds?.forEach((id: number) => {
            roles.groups.push({
                id,
                data: (form.value.serviceId ? { serviceIds: [form.value.serviceId] } : {}) as any,
            });
        });
        form.value.ruleIds?.forEach((id: string) => {
            const { resource, action } = parseRuleId(id);
            roles.rules.push({
                action,
                resource,
                data: (form.value.serviceId ? { serviceIds: [form.value.serviceId] } : {}) as any,
            });
        });
    }

    private updateUserGroups() {
        const { rules, groups } = this.getRoles();
        const data = {
            id: routeParams.id(),
            rules,
            groups,
            organizationId: this.store.globalGlobalStore.organizationStore.selectedId(),
        } as UserUpdateInput;
        this.store.api.updateUser(data).then(() => {
            contentGlobalSuccess.next('acl.alert.employmentToGroupChange');
            this.store.refresh();
        });
    }

}
